import { FC, useEffect, useState } from 'react';
import { StudentSkillAssignment } from '../../models/Assignment';
import { Button } from 'primereact/button';
import { Rating } from 'primereact/rating';
import { mapSkillStatus } from '../../businessLogicService/mapSkillStatus';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../redux/hooks';
import { selectedStudentSkillStateAfterTakeAssessment } from '../../redux/reducers/studentSkillAfterTakeAssessmentSlice';

interface Props {
  studentSkillAssignment: StudentSkillAssignment;
  index: number;
  lastIndex: number;
  isPast: boolean;
}

export const AssignmentStudentCard: FC<Props> = ({ studentSkillAssignment, index, lastIndex, isPast }) => {
  const [localStudentSkillAssignment, setLocalStudentSkillAssignment] = useState<StudentSkillAssignment | null>(null);
  const reduxSkillState = useAppSelector(selectedStudentSkillStateAfterTakeAssessment);
  const navigate = useNavigate();
  const parseLearnosityScoresIntoStars = (score: number) => (score ? score / 2 : 0);

  useEffect(() => {
    if (
      studentSkillAssignment.skillAssessment === null &&
      reduxSkillState.nextState?.skill.id === studentSkillAssignment.skill.id
    ) {
      const payload: StudentSkillAssignment = {
        ...studentSkillAssignment,
        skillAssessment: {
          score: reduxSkillState.nextState.skillAssessment?.score,
        },
      };
      setLocalStudentSkillAssignment(payload);
    } else {
      setLocalStudentSkillAssignment(studentSkillAssignment);
    }
  }, [studentSkillAssignment, reduxSkillState]);

  const { className, status } = mapSkillStatus('', localStudentSkillAssignment?.skillAssessment?.score);

  const btnLabel = () => {
    if (localStudentSkillAssignment?.submittedOn && !localStudentSkillAssignment?.skillAssessment) {
      return 'View';
    } else if (localStudentSkillAssignment?.skillAssessment?.score) {
      if (localStudentSkillAssignment?.skillAssessment?.score < 8) {
        return 'Try Again';
      } else {
        return 'View';
      }
    } else {
      return 'Start';
    }
  };
  const starsValue = parseLearnosityScoresIntoStars(localStudentSkillAssignment?.skillAssessment?.score || 0);
  const starsAriaLabel = `${starsValue} stars filled out of 5`;
  const safeContrastText = 'tw-text-[#0C1B54]';
  return (
    <>
      {index === 0 ? (
        <div className="col-12 sm:col-3 mb-5 px-0 pb-0 flex bg-link flex-column h-14rem border-2 border-round-xl border-primary justify-content-end">
          <h4 className={`text-center tw-text-[14px] tw-mb-3 mt-2 font-semibold ${safeContrastText}`}>
            Start here! <span>🏁</span>
          </h4>
          <div className="w-full mx-0 bg-white flex flex-column h-11rem border-top-2 border-round-xl border-primary justify-content-between">
            <p
              className={`w-full text-center font-semibold ${safeContrastText}`}
            >{`${localStudentSkillAssignment?.skill.name} (${localStudentSkillAssignment?.skill.code})`}</p>
            {localStudentSkillAssignment?.skillAssessment !== null && (
              <div className="nc_result-ratings flex flex-column align-items-center justify-content-center">
                <span role="img" aria-label={starsAriaLabel}>
                  <Rating
                    cancel={false}
                    stars={5}
                    value={starsValue}
                    readOnly
                    className={`${className}-text`}
                    aria-hidden="true"
                  />
                </span>
                <span className={`${className}-text font-semibold my-2`}>{status}</span>
              </div>
            )}
            {localStudentSkillAssignment?.submittedOn && !localStudentSkillAssignment.skillAssessment && (
              <div className="flex align-items-center justify-content-center">
                <span className="nc_passed-text font-semibold mb-3">Passed</span>
              </div>
            )}
            {!isPast && (
              <Button
                outlined
                className="col-11 mx-auto mb-2"
                label={btnLabel()}
                onClick={() => navigate(`/skill/${localStudentSkillAssignment?.skill.code}`)}
                aria-label={`${btnLabel()} - ${localStudentSkillAssignment?.skill.name}`}
              />
            )}
          </div>
        </div>
      ) : index === lastIndex ? (
        <div className="col-12 sm:col-3 mb-5 px-0 pb-0 flex bg-orange-light flex-column h-14rem border-2 border-round-xl border-canary justify-content-end">
          <h4 className="text-center font-semibold tw-text-[14px] tw-mb-3 mt-2">
            Target Skill <span>🏅</span>
          </h4>
          <div className="w-full mx-0 flex flex-column h-11rem border-top-2 bg-white border-round-xl border-canary justify-content-between">
            <p className="w-10 mx-auto text-center font-semibold h-1rem white-space-nowrap overflow-hidden text-overflow-ellipsis">{`${localStudentSkillAssignment?.skill.name} (${localStudentSkillAssignment?.skill.code})`}</p>
            {localStudentSkillAssignment?.skillAssessment !== null && (
              <div className="nc_result-ratings flex flex-column align-items-center justify-content-center">
                <span role="img" aria-label={starsAriaLabel}>
                  <Rating
                    cancel={false}
                    stars={5}
                    value={starsValue}
                    readOnly
                    className={`${className}-text`}
                    aria-hidden="true"
                  />
                </span>
                <span className={`${className}-text font-semibold my-2`}>{status}</span>
              </div>
            )}
            {localStudentSkillAssignment?.submittedOn && !localStudentSkillAssignment.skillAssessment && (
              <div className="flex align-items-center justify-content-center">
                <span className="nc_passed-text font-semibold mb-3">Passed</span>
              </div>
            )}
            {!isPast && (
              <Button
                outlined
                className="col-11 mx-auto mb-2"
                label={btnLabel()}
                onClick={() => navigate(`/skill/${localStudentSkillAssignment?.skill.code}`)}
                aria-label={`${btnLabel()} - ${localStudentSkillAssignment?.skill.name}`}
              />
            )}
          </div>
        </div>
      ) : (
        <div className="col-12 sm:col-3 mb-5 px-0 pb-0 flex flex-column h-12rem border-2 border-round-xl border-gray-400 justify-content-between">
          <p className="w-full text-center font-semibold">{`${localStudentSkillAssignment?.skill.name} (${localStudentSkillAssignment?.skill.code})`}</p>
          {localStudentSkillAssignment?.skillAssessment !== null && (
            <div className="nc_result-ratings flex flex-column align-items-center justify-content-center">
              <span role="img" aria-label={starsAriaLabel}>
                <Rating
                  cancel={false}
                  stars={5}
                  value={starsValue}
                  readOnly
                  className={`${className}-text`}
                  aria-hidden="true"
                />
                <span className={`${className}-text font-semibold my-2`}>{status}</span>
              </span>
            </div>
          )}
          {localStudentSkillAssignment?.submittedOn && !localStudentSkillAssignment.skillAssessment && (
            <div className="flex align-items-center justify-content-center">
              <span className="nc_passed-text font-semibold mb-3">Passed</span>
            </div>
          )}
          {!isPast && (
            <Button
              outlined
              className="col-11 mx-auto mb-2"
              label={btnLabel()}
              onClick={() => navigate(`/skill/${localStudentSkillAssignment?.skill.code}`)}
              aria-label={`${btnLabel()} - ${localStudentSkillAssignment?.skill.name}`}
            />
          )}
        </div>
      )}
      {index !== lastIndex && (index + 1) % 3 !== 0 && (
        <div className="relative flex" style={{ top: -120 }}>
          <i className="nc_icon-nc_right icon-md mx-0 px-0 text-primary" />
          <i className="nc_icon-nc_right icon-md mx-0 px-0 text-primary" />
        </div>
      )}
    </>
  );
};
